import {Component,OnDestroy,OnInit} from "@angular/core";
import {PageHeaderItem} from "@share/component/page-header/page-header";
import {Store} from "@ngrx/store";
import {AppState} from "@domain/appstate";
import {Session} from "@domain/security/session";
import {ActivatedRoute,Router} from "@angular/router";
import {isRouteAllowed} from "@core/security/role-admin-helpers";
import {filterFirstNotNull} from "@share/utils/rxjs-custom-operator";
import {IComponentWithRoutedTabs} from "@domain/admin/recherche/component-with-routed-tabs";
import {Subscription} from "rxjs";
import {watchRouteParams} from "@share/utils/watch-route-params";

/**
 * Enum pour les noms des différents onglets de la page
 */
export enum Onglets {
    LOGO = "Logo",
    BOITE_LIEN = "BoiteLien"
}

/**
 * Sous-menu personnalisation
 *
 * @author Guillaume TRAVOT
 * @date 26/04/2024
 */
@Component({
    host: {'data-test-id': 'parametre-personnalisation'},
    templateUrl: './parametre-personnalisation.component.html'
})
export class ParametrePersonnalisationComponent implements OnInit, OnDestroy, IComponentWithRoutedTabs {
    /** Liste statique des onglets */
    public static listeOnglets: Array<PageHeaderItem> = [
        {
            code: Onglets.LOGO,
            libelle: 'admin.parametres.personnalisation.onglets.logos',
            url: 'Admin/Parametre/Personnalisation/Logo'
        },
        {
            code: Onglets.BOITE_LIEN,
            libelle: 'admin.parametres.personnalisation.onglets.boiteLiens',
            url: 'Admin/Parametre/Personnalisation/BoiteLien'
        }
    ];

    /** Liste des différents onglets disponibles */
    listeTabItems: Array<PageHeaderItem> = ParametrePersonnalisationComponent.listeOnglets;

    /** Onglet courant */
    selectedItem: PageHeaderItem;

    /** Référence vers l'enum des onglets pour l'utiliser dans le template */
    Onglet = Onglets;

    /** Index de l'onglet courant */
    selectedIndex: number;

    /** Session */
    private session: Session;

    /** Souscription */
    private subscription: Subscription;

    constructor(
        private router: Router,
        private store: Store<AppState>,
        private activatedRoute: ActivatedRoute,
    ) { }

    /**
     * Initialisation du composant
     */
    ngOnInit(): void {
        //Récupération de la session
        this.store.select(state => state.session).pipe(filterFirstNotNull()).subscribe((session: Session) => {
            //Stockage de la session
            this.session = session;

            //Filtrage des onglets autorisés
            this.listeTabItems = this.listeTabItems.filter(i => isRouteAllowed(this.router, this.session, i.url));

            //Abonnement aux changements de route
            this.subscription = watchRouteParams(this.router,this.activatedRoute).subscribe(() => {
                //Récupération de la route
                const firstChild = this.activatedRoute.snapshot.firstChild;
                
                //Récupération du path
                const segment = firstChild.url[0].path;

                //On vérifie si on trouve l'onglet demandé via l'url
                const matching = Object.values(Onglets).find(v => v === segment);

                if (!!matching) {
                    //Si on trouve bien l'onglet correspondant, on met à jour le composant header
                    this.selectedItem = this.listeTabItems.find(i => i.code === segment);
                    this.selectedIndex = this.listeTabItems.findIndex(i => i.code === segment);
                } else {
                    //Sinon, on redirige sur le premier onglet et on met à jour le composant header
                    this.selectedItem = this.listeTabItems[0];
                    this.selectedIndex = 0;
                }
            });
        });
    }

    /**
     * Changement d'onglet
     *
     * @param selectedItem Onglet sélectionné
     */
    onSelectedItemChange(selectedItem: PageHeaderItem): void {
        if (!this.selectedItem) {
            this.selectedItem = selectedItem;
        }
        if (selectedItem?.code !== this.selectedItem?.code) {
            this.selectedItem = selectedItem;
            this.router.navigateByUrl(selectedItem.url);
        }
    }

    /**
     * Destruction du composant
     */
    ngOnDestroy() {
        //Résiliation de l'abonnement
        this.subscription.unsubscribe();
    }
}
